import { FC, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Header } from 'components';
import Table from 'components/tables/table';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import useApi from 'utils/use-api';
import useTableData from 'utils/use-table-data';
import { User } from 'types/user';
import { Role } from 'types/role';
import Modal from 'components/layouts/modal';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextInput from 'components/core/text-input';
import { SelectInput } from 'components/core';
import wrapClick from 'utils/wrap-click';
import dayjs from 'dayjs';
import { useUrlState, withPermissions, hasPermission } from 'utils';

const UserFormModal: FC<{
  open: boolean;
  setOpen: (v: boolean) => void;
  roles: Role[];
  initialValues: any;
  onSubmit: (values: any) => Promise<void>;
  title: string;
  actionLabel: string;
  disableEmail?: boolean;
  hideInviteToggle?: boolean;
}> = ({ open, setOpen, roles, initialValues, onSubmit, title, actionLabel, disableEmail, hideInviteToggle }) => {
  const form = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      email: Yup.string()
        .email()
        .required()
        .test('allowed-domain', 'Email must be from @wewire.com or @ogateway.co', value => {
          if (!value) return false;
          return value.toLowerCase().endsWith('@wewire.com') || value.toLowerCase().endsWith('@ogateway.co');
        }),
      firstName: Yup.string().required(),
      lastName: Yup.string().required(),
      phoneNumber: Yup.string().optional(),
      roleId: Yup.number().required('Role is required'),
      isActive: Yup.boolean().optional(),
      sendInvite: Yup.boolean().optional(),
    }),
    onSubmit: async values => {
      await onSubmit(values);
      setOpen(false);
    },
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={title}
      description=""
      size="4xl"
      renderActions={() => (
        <button
          type="button"
          onClick={wrapClick(() => form.handleSubmit())}
          className="inline-flex justify-center rounded-md  ml-3 border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm"
        >
          {actionLabel}
        </button>
      )}
    >
      <div className="space-y-4">
        <TextInput id="email" label="Email" required readOnly={disableEmail} disabled={disableEmail} {...form} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextInput id="firstName" label="First Name" required {...form} />
          <TextInput id="lastName" label="Last Name" required {...form} />
        </div>
        <TextInput id="phoneNumber" label="Phone Number" {...form} />
        <SelectInput
          id="roleId"
          label="Role"
          options={roles.map(role => ({ value: role.id, label: role.name }))}
          placeholder="Select a role"
          {...form}
        />
        <div className="flex items-center space-x-6">
          <label className="inline-flex items-center space-x-2 text-sm text-gray-700">
            <input
              id="isActive"
              name="isActive"
              type="checkbox"
              checked={form.values.isActive ?? true}
              onChange={form.handleChange}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>Active</span>
          </label>
          {!hideInviteToggle && (
            <label className="inline-flex items-center space-x-2 text-sm text-gray-700">
              <input
                id="sendInvite"
                name="sendInvite"
                type="checkbox"
                checked={form.values.sendInvite ?? true}
                onChange={form.handleChange}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>Send invite email</span>
            </label>
          )}
        </div>
      </div>
    </Modal>
  );
};

const UsersPage: FC = () => {
  const api = useApi();
  const [skip, setSkip] = useState(0);
  const [limit] = useUrlState<number>('pageSize');
  const [modal, setModal] = useUrlState<string>('modal');
  const [selectedId, setSelectedId] = useUrlState<number>('id');

  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const res = await api.get('/roles');
      return res.data as Role[];
    },
    enabled: hasPermission(['role:view']),
  });

  const {
    data: usersResponse,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: ['users', skip, limit],
    queryFn: async () => {
      const res = await api.get('/users', { params: { skip, limit: limit || 10 } });
      return res.data as { rows: User[]; total: number };
    },
  });

  const usersData = useTableData({
    rows: usersResponse?.rows || [],
    count: usersResponse?.total || 0,
  });

  const createUserMutation = useMutation({
    mutationFn: async (values: any) => {
      const res = await api.post('/users', values);
      return res.data;
    },
    onSuccess: () => refetch(),
  });

  const updateUserMutation = useMutation({
    mutationFn: async ({ id, values }: { id: number; values: any }) => {
      const res = await api.put(`/users/${id}`, values);
      return res.data;
    },
    onSuccess: () => refetch(),
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await api.delete(`/users/${id}`);
      return res.data;
    },
    onSuccess: () => refetch(),
  });

  const handleCreate = async (values: any) => {
    await createUserMutation.mutateAsync(values);
  };

  const handleEdit = async (values: any) => {
    if (selectedId) {
      const { firstName, lastName, phoneNumber, roleId, isActive } = values;
      await updateUserMutation.mutateAsync({
        id: Number(selectedId),
        values: { firstName, lastName, phoneNumber, roleId, isActive },
      });
    }
  };

  const handleDelete = (id: number) => deleteUserMutation.mutate(id);

  const TableColumns = () => (
    <tr>
      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Email</th>
      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Full Name</th>
      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Phone</th>
      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Role</th>
      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Last Login</th>
      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Created</th>
      <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
        <span className="sr-only">Actions</span>
      </th>
    </tr>
  );

  const TableRow = (user: User) => {
    const statusBadge =
      user.isActive !== false ? (
        <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset bg-green-50 text-green-700 ring-green-600/20">
          Active
        </span>
      ) : (
        <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset bg-gray-50 text-gray-700 ring-gray-600/20">
          Inactive
        </span>
      );

    const formatDate = (val?: string) => (val ? dayjs(val).format('MMM DD, YYYY') : '—');

    return (
      <tr className="hover:bg-gray-50">
        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{user.email}</td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{[user.firstName, user.lastName].filter(Boolean).join(' ')}</td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{user.phoneNumber || '—'}</td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{user.role?.name || '—'}</td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{statusBadge}</td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatDate(user.lastLogin)}</td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatDate(user.createdAt)}</td>
        <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 space-x-3">
          {withPermissions(['user:update'])(
            <button
              onClick={() => {
                setSelectedId(user.id as any);
                setModal('update' as any);
              }}
              className="inline-flex items-center gap-x-1 text-primary-600 hover:text-primary-900"
            >
              <PencilSquareIcon className="h-4 w-4" />
              Edit
            </button>,
          )}
          {withPermissions(['user:delete'])(
            <button
              onClick={() => {
                setSelectedId(user.id as any);
                setModal('delete' as any);
              }}
              className="inline-flex items-center gap-x-1 text-red-600 hover:text-red-900"
            >
              <TrashIcon className="h-4 w-4" />
              Delete
            </button>,
          )}
        </td>
      </tr>
    );
  };

  const roleOptions = useMemo(() => rolesData || [], [rolesData]);
  const baseFormValues = {
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    roleId: undefined,
    isActive: true,
    sendInvite: true,
  };

  return (
    <main className="flex-1 flex flex-col overflow-hidden h-screen bg-gray-50">
      <Header
        renderActions={() => (
          <>
            {withPermissions(['user:create'])(
              <button
                type="button"
                onClick={() => setModal('create' as any)}
                className="inline-flex items-center gap-x-2 rounded-md bg-primary-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              >
                <PlusIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                New User
              </button>,
            )}
          </>
        )}
      />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 min-h-full mx-auto p-4 sm:p-6 overflow-y-auto bg-gray-50">
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12 lg:gap-8">
            <div className="col-span-12">
              <Table
                title="Users"
                data={usersData || { rows: [], total: 0, page: 1, pageSize: limit || 10, totalPages: 0 }}
                loading={isLoading}
                isRefetching={isRefetching}
                skip={skip}
                limit={limit || 10}
                setSkip={setSkip}
                showTableHeader={true}
                hasSearch={false}
                refetch={refetch}
                renderColumns={TableColumns}
                renderItem={TableRow}
              />
            </div>
          </div>
        </div>
      </div>

      {modal === ('create' as any) && (
        <UserFormModal
          open={true}
          setOpen={v => {
            if (!v) setModal(undefined as any);
          }}
          roles={roleOptions}
          initialValues={baseFormValues}
          onSubmit={handleCreate}
          title="Create User"
          actionLabel="Create User"
        />
      )}
      {modal === ('update' as any) && selectedId && (
        <UserFormModal
          open={true}
          setOpen={v => {
            if (!v) {
              setModal(undefined as any);
              setSelectedId(undefined as any);
            }
          }}
          roles={roleOptions}
          initialValues={{
            email: usersResponse?.rows.find(u => u.id === Number(selectedId))?.email || '',
            firstName: usersResponse?.rows.find(u => u.id === Number(selectedId))?.firstName || '',
            lastName: usersResponse?.rows.find(u => u.id === Number(selectedId))?.lastName || '',
            phoneNumber: usersResponse?.rows.find(u => u.id === Number(selectedId))?.phoneNumber || '',
            roleId: usersResponse?.rows.find(u => u.id === Number(selectedId))?.roleId,
            isActive: usersResponse?.rows.find(u => u.id === Number(selectedId))?.isActive ?? true,
            sendInvite: false,
          }}
          disableEmail={true}
          hideInviteToggle={true}
          onSubmit={handleEdit}
          title="Edit User"
          actionLabel="Save Changes"
        />
      )}
      {modal === ('delete' as any) && selectedId && (
        <Modal
          open={true}
          setOpen={v => {
            if (!v) {
              setModal(undefined as any);
              setSelectedId(undefined as any);
            }
          }}
          title="Delete User"
          description="Are you sure you want to delete this user?"
          size="4xl"
          renderActions={() => (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  handleDelete(Number(selectedId));
                  setModal(undefined as any);
                  setSelectedId(undefined as any);
                }}
                className="inline-flex justify-center rounded-md border ml-3 border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
              >
                Delete
              </button>
            </div>
          )}
        />
      )}
    </main>
  );
};

export default UsersPage;
